import { Affiliate } from './Affiliate';
import { ClientCredits } from './ClientCredits';

/**
 * Mesmo formato do bloco `wallet` de `PartnerService::getCredits` (CU-17tm466u0vy):
 * `available` chega como STRING decimal ("3786.80") ou `null` (carteira indisponível
 * agora — nunca "0" inventado).
 */
export interface WalletBlock {
  billed: Array<'sms' | 'call' | 'whatsapp' | 'ai'>;
  currency: string;
  available: string | null;
}

export interface CreditsBlock {
  contracted: number;
  sent: number;
  /** `null` só quando migrada e a carteira não respondeu agora. */
  available: number | null;
  /** `true` quando o canal foi migrado e este número é o "≈" calculado pelo preço da conta. */
  deprecated?: boolean;
}

export type BalanceKind = 'wallet' | 'credits';

export type BalanceLevel = 'ok' | 'low' | 'zero' | 'unavailable';

/**
 * View-model único para lista, gaveta e Estatísticas (decisão do mockup aprovado em
 * 27/10): uma conta migrada e uma não migrada passam pela MESMA função e o componente
 * de saldo (`account-balance`) não decide regra nenhuma, só exibe.
 */
export interface ManagedBalanceView {
  kind: BalanceKind;
  level: BalanceLevel;
  /** Carteira em R$ — só quando `kind === 'wallet'`. */
  wallet?: WalletBlock;
  sms?: CreditsBlock;
  call?: CreditsBlock;
  /** SMS equivalentes usados para ordenar/filtrar; `null` = indisponível. */
  smsEquivalent: number | null;
}

export const LOW_WALLET_REAIS = 50;
export const LOW_SMS_CREDITS = 500;

/**
 * Deriva o nível do selo a partir do que a API já decidiu (`balance_level`) quando
 * presente; senão recalcula pelas mesmas constantes do back (§ decisões do Bruno,
 * 27/10): carteira < R$ 50 OU créditos de SMS < 500 é `low`; `unavailable` nunca
 * entra nessa conta.
 */
function levelFor(kind: BalanceKind, value: number | null, explicitLevel?: BalanceLevel): BalanceLevel {
  if (explicitLevel) {
    return explicitLevel;
  }

  if (value === null) {
    return 'unavailable';
  }

  if (value <= 0) {
    return 'zero';
  }

  const threshold = kind === 'wallet' ? LOW_WALLET_REAIS : LOW_SMS_CREDITS;

  return value < threshold ? 'low' : 'ok';
}

/**
 * Lê o payload de `/users/affiliates` (lista/gaveta) ou de `/partner/statistics/:id/credits`
 * (Estatísticas, mesmo formato do `/credits` externo) e devolve o MESMO view-model.
 * Os campos chegam em snake_case direto da API (sem transformação, convenção do projeto).
 */
export function toBalanceView(source: Partial<Affiliate> & Partial<ClientCredits>): ManagedBalanceView {
  // A presença do bloco `wallet` NÃO basta: ele também aparece numa conta não
  // migrada quando só WhatsApp/IA estão na carteira (`PartnerService::getCredits`).
  // O que decide "esta conta é migrada" é `balance_kind` (contrato novo) ou,
  // quando ele não vem (ex.: `/credits`), o `deprecated: true` do SMS — o selo
  // que o back já usa para marcar "isto é um ≈ pelo preço da conta".
  const kind: BalanceKind = source.balance_kind ?? (source.sms?.deprecated ? 'wallet' : 'credits');

  if (kind === 'wallet') {
    const available = source.wallet?.available === null || source.wallet?.available === undefined
      ? null
      : Number(source.wallet.available);
    const smsEquivalent = source.sms_equivalent ?? (source.sms?.available ?? null);

    return {
      kind,
      level: levelFor('wallet', available, source.balance_level),
      wallet: source.wallet,
      sms: source.sms,
      call: source.call,
      smsEquivalent,
    };
  }

  // Sem o bloco `sms` (front no ar antes do back !1783, ou uma listagem que não
  // decora o saldo) não há saldo para mostrar: é "indisponível", nunca um 0
  // inventado — o "SALDO: 0" falso é exatamente o que esta task corrige.
  const smsAvailable = source.sms?.available;

  if (smsAvailable === null || smsAvailable === undefined) {
    return {
      kind: 'credits',
      level: 'unavailable',
      wallet: source.wallet,
      sms: source.sms,
      call: source.call,
      smsEquivalent: null,
    };
  }

  return {
    kind: 'credits',
    level: levelFor('credits', smsAvailable, source.balance_level),
    wallet: source.wallet,
    sms: source.sms,
    call: source.call,
    smsEquivalent: smsAvailable,
  };
}
